/**
 * Copyright Higharc Inc. 2019
 *
 * SSAA accumulation shader.
 */
import * as THREE from "three";
import type { ScreenSpaceViewPositionDefines, ScreenSpaceViewPositionUniforms } from "./screenSpaceHelpersGLSL";
import {
  screenSpaceViewPositionDefineDefaults,
  screenSpaceViewPositionGLSL,
  screenSpaceViewPositionUniformDefaults,
} from "./screenSpaceHelpersGLSL";
import type { ToneMappingDefines } from "./ToneMappingDefines";
import { UVVertexShader } from "./UVVertexShader";

interface SSAAAccumulateShaderDefines extends ScreenSpaceViewPositionDefines, ToneMappingDefines {
  REPROJECT: number; // eslint-disable-line @typescript-eslint/naming-convention
  YCOCG_CLAMP: number; // eslint-disable-line @typescript-eslint/naming-convention
  SIMPLE_CLAMP: number; // eslint-disable-line @typescript-eslint/naming-convention
  USE_PREV_DEPTH_IN_REPROJECT: number; // eslint-disable-line @typescript-eslint/naming-convention
  BLEND_WITH_NEIGHBORS: number; // eslint-disable-line @typescript-eslint/naming-convention
}

interface SSAAAccumulateShaderUniforms extends ScreenSpaceViewPositionUniforms {
  tSample: THREE.IUniform;
  tPrevious: THREE.IUniform;
  tPrevDepth: THREE.IUniform;
  weight: THREE.IUniform;

  ssfxToneMappingEnabled: THREE.IUniform;
  toneMappingExposure: THREE.IUniform;

  inverseViewMatrix: THREE.IUniform;
  prevViewMatrix: THREE.IUniform;
  prevViewProjectionMatrix: THREE.IUniform;
  pixelSize: THREE.IUniform;
  accumulateIndex: THREE.IUniform;
}

interface SSAAAccumulateShaderType {
  defines: SSAAAccumulateShaderDefines;
  uniforms: SSAAAccumulateShaderUniforms;
  vertexShader: string;
  fragmentShader: string;
}

export const SSAAAccumulateShader: SSAAAccumulateShaderType = {
  defines: {
    ...screenSpaceViewPositionDefineDefaults,
    REPROJECT: 1,
    YCOCG_CLAMP: 1, // If set to 1, color clamping will be done in YCoCg space.
    SIMPLE_CLAMP: 0, // If set to 1, will use simple AABB color clamping instead of projecting the color sample to the AABB.
    USE_PREV_DEPTH_IN_REPROJECT: 1,
    BLEND_WITH_NEIGHBORS: 1,
    DM_LINEAR_TONE_MAPPING: 0,
    DM_REINHARD_TONE_MAPPING: 0,
    DM_CINEON_TONE_MAPPING: 0,
    DM_ACES_FILMIC_TONE_MAPPING: 0,
  },

  uniforms: {
    ...screenSpaceViewPositionUniformDefaults,
    tSample: { value: null },
    tPrevious: { value: null },
    tPrevDepth: { value: null },
    weight: { value: 0.1 },

    ssfxToneMappingEnabled: { value: false },
    toneMappingExposure: { value: 1.0 },

    inverseViewMatrix: { value: new THREE.Matrix4() },
    prevViewMatrix: { value: new THREE.Matrix4() },
    prevViewProjectionMatrix: { value: new THREE.Matrix4() },
    pixelSize: { value: new THREE.Vector2(0.01, 0.01) },
    accumulateIndex: { value: 0 }, // How many samples of the frame have been accumulated with the same static camera position / scene.
  },

  vertexShader: UVVertexShader,

  fragmentShader: `#include <common>

#include <tonemapping_pars_fragment>

#if defined(DM_LINEAR_TONE_MAPPING) && DM_LINEAR_TONE_MAPPING == 1
  #define SSAA_TONE_MAPPING(x) LinearToneMapping(x)
#elif defined(DM_REINHARD_TONE_MAPPING) && DM_REINHARD_TONE_MAPPING == 1
  #define SSAA_TONE_MAPPING(x) ReinhardToneMapping(x)
#elif defined(DM_CINEON_TONE_MAPPING) && DM_CINEON_TONE_MAPPING == 1
  #define SSAA_TONE_MAPPING(x) OptimizedCineonToneMapping(x)
#elif defined(DM_ACES_FILMIC_TONE_MAPPING) && DM_ACES_FILMIC_TONE_MAPPING == 1
  #define SSAA_TONE_MAPPING(x) ACESFilmicToneMapping(x)
#endif

uniform float weight;

uniform sampler2D tSample;

#if defined(SSAA_TONE_MAPPING)
uniform bool ssfxToneMappingEnabled;
#endif

uniform sampler2D tPrevious;

#if USE_PREV_DEPTH_IN_REPROJECT
uniform sampler2D tPrevDepth;
#endif

varying vec2 vUv;

#if REPROJECT == 1
#include <packing>

${screenSpaceViewPositionGLSL}

uniform vec2 pixelSize;

uniform mat4 inverseViewMatrix;
uniform float accumulateIndex;

uniform mat4 prevViewMatrix;
uniform mat4 prevViewProjectionMatrix;

#endif

const mat3 RGBToYCoCg = mat3(0.25, 0.5, -0.25, 0.5, 0, 0.5, 0.25, -0.5, -0.25);
const mat3 YCoCgToRGB = mat3(1, 1, 1, 1, 0, -1, -1, 1, -1);

#if YCOCG_CLAMP == 1
#define TO_CLAMP_SPACE(RGB) RGBToYCoCg * RGB
#define FROM_CLAMP_SPACE(YCoCg) YCoCgToRGB * YCoCg
#else
#define TO_CLAMP_SPACE(RGB) RGB
#define FROM_CLAMP_SPACE(RGB) RGB
#endif

#if SIMPLE_CLAMP == 0
vec3 clip_aabb(vec3 value, vec3 aabb_min, vec3 aabb_max) {
  vec3 aabbCenter = 0.5 * (aabb_min + aabb_max);
  vec3 aabbHalfSize = 0.5 * (aabb_max - aabb_min);
  vec3 fromCenter = value - aabbCenter;
  // Adding 0.001 is just to guard against division by zero.
  vec3 normalizedFromCenter = (fromCenter + 0.001) / (aabbHalfSize + 0.001);
  vec3 absUnit = abs(normalizedFromCenter);
  float divisor = max(absUnit.x, max(absUnit.y, absUnit.z));
  if (divisor > 1.0) {
    // Clip towards the AABB center.
    return aabbCenter + fromCenter / divisor;
  } else {
    // Point is inside the AABB.
    return value;
  }
}
#endif

void main() {
  vec2 prevUv = vUv;
#if REPROJECT == 1
  // If we've already accumulated one sample of this frame then we don't need to reproject anymore.
  bool isReprojecting = accumulateIndex < 1.0;
  float prevViewZ = 0.0;
  if (isReprojecting) {
    float viewZ = getViewZ(GET_DEPTH(tDepth, vUv));
    vec3 viewPos = getViewPosition(vUv, viewZ);
    vec4 worldPos = inverseViewMatrix * vec4(viewPos, 1.0);
    prevViewZ = (prevViewMatrix * worldPos).z;
    vec4 prevProjectedPos = prevViewProjectionMatrix * worldPos;
    #if PERSPECTIVE_CAMERA == 1
      prevUv = (prevProjectedPos.xy / prevProjectedPos.w) * 0.5 + 0.5;
    #else
      prevUv = (prevProjectedPos.xy) * 0.5 + 0.5;
    #endif
  }
#endif
  vec4 previousTexel = texture2D(tPrevious, prevUv);
  vec4 texel = texture2D(tSample, vUv);
#if defined(SSAA_TONE_MAPPING)
  // If we render samples into a floating point framebuffer, then tonemap here.
  if (ssfxToneMappingEnabled) {
    texel.rgb = SSAA_TONE_MAPPING(texel.rgb);
  }
#endif
#if REPROJECT == 1
  if (prevUv.x < 0.0 || prevUv.x > 1.0 || prevUv.y < 0.0 || prevUv.y > 1.0) {
    previousTexel.a = 0.0;
  }
#if USE_PREV_DEPTH_IN_REPROJECT == 1
  if (isReprojecting && previousTexel.a > 0.0) {
    float neighbor1ViewZ = getViewZ(GET_DEPTH(tPrevDepth, prevUv + vec2(pixelSize.x, 0)));
    float neighbor2ViewZ = getViewZ(GET_DEPTH(tPrevDepth, prevUv + vec2(-pixelSize.x, 0)));
    float neighbor3ViewZ = getViewZ(GET_DEPTH(tPrevDepth, prevUv + vec2(0, pixelSize.y)));
    float neighbor4ViewZ = getViewZ(GET_DEPTH(tPrevDepth, prevUv + vec2(0, -pixelSize.y)));
    float maxViewZ = max(max(neighbor1ViewZ, neighbor2ViewZ), max(neighbor3ViewZ, neighbor4ViewZ));
    float minViewZ = min(min(neighbor1ViewZ, neighbor2ViewZ), min(neighbor3ViewZ, neighbor4ViewZ));
    // Note that all the viewZ values are negative, so the multipliers increase the tolerance.
    // Rather high tolerances are used, since the error can accumulate from multiple steps.
    if (prevViewZ < minViewZ * 1.02 || prevViewZ > maxViewZ * 0.98) {
      previousTexel.a = 0.0;
    }
  }
#endif // USE_PREV_DEPTH_IN_REPROJECT
  if (isReprojecting) {
    vec3 neighbor1 = texture2D(tSample, vUv + vec2(pixelSize.x, 0)).rgb;
    vec3 neighbor2 = texture2D(tSample, vUv + vec2(-pixelSize.x, 0)).rgb;
    vec3 neighbor3 = texture2D(tSample, vUv + vec2(0, pixelSize.y)).rgb;
    vec3 neighbor4 = texture2D(tSample, vUv + vec2(0, -pixelSize.y)).rgb;

#if defined(SSAA_TONE_MAPPING)
    if (ssfxToneMappingEnabled) {
      neighbor1 = SSAA_TONE_MAPPING(neighbor1);
      neighbor2 = SSAA_TONE_MAPPING(neighbor2);
      neighbor3 = SSAA_TONE_MAPPING(neighbor3);
      neighbor4 = SSAA_TONE_MAPPING(neighbor4);
    }
#endif

    if (previousTexel.a > 0.0) {
      vec3 neighbor1Clip = TO_CLAMP_SPACE(neighbor1);
      vec3 neighbor2Clip = TO_CLAMP_SPACE(neighbor2);
      vec3 neighbor3Clip = TO_CLAMP_SPACE(neighbor3);
      vec3 neighbor4Clip = TO_CLAMP_SPACE(neighbor4);
      vec3 texelClip = TO_CLAMP_SPACE(texel.rgb);
      vec3 maxNeighbor = max(texelClip, max(max(neighbor1Clip, neighbor2Clip), max(neighbor3Clip, neighbor4Clip)));
      vec3 minNeighbor = min(texelClip, min(min(neighbor1Clip, neighbor2Clip), min(neighbor3Clip, neighbor4Clip)));

      vec3 previousClip = TO_CLAMP_SPACE(previousTexel.rgb);

      #if SIMPLE_CLAMP == 1
        previousTexel.rgb = FROM_CLAMP_SPACE(clamp(previousClip, minNeighbor, maxNeighbor));
      #else
        previousTexel.rgb = FROM_CLAMP_SPACE(clip_aabb(previousClip, minNeighbor, maxNeighbor));
      #endif
    }

#if BLEND_WITH_NEIGHBORS == 1
    // Blend with neighbors to smooth out noise a bit.
    texel.rgb = texel.rgb * 0.6 + (neighbor1 + neighbor2 + neighbor3 + neighbor4) * 0.25 * 0.4;
#endif
  }
#endif // REPROJECT

#if REPROJECT == 1
  float useWeight = previousTexel.a > 0.0 ? weight : 1.0;
  gl_FragColor = previousTexel * (1.0 - useWeight) + texel * useWeight;
#else
  gl_FragColor.a = previousTexel.a * (1.0 - weight) + texel.a * weight;
  // Mix the color values together by weight in premultiplied alpha space, and then un-premultiply.
  gl_FragColor.rgb = (previousTexel.rgb * previousTexel.a * (1.0 - weight) + texel.rgb * texel.a * weight) / gl_FragColor.a;
#endif
  // Prevent overflow to infinity (half-float maxes out around 64K)
  gl_FragColor = clamp(gl_FragColor, 0.0, 64000.0);
  #include <colorspace_fragment>
}`,
} as const;
